import { useEffect, useState, type ReactNode } from 'react';
import { bannerFor, type Connection } from '@mailbutler/ux';
import { useI18n } from '../i18n/I18nProvider.js';

/**
 * Whether the screen is still true.
 *
 * The failure this is for is not "we went offline" — a mail client is used on trains and in
 * basements, and that happens several times a day. It is **"we came back and the screen was still
 * lying"**.
 *
 * It never blocks and it never covers anything. Everything already rendered is still true; it is
 * just not getting any newer, and hiding it behind a modal takes away what somebody has.
 */
export function ConnectionBanner({ lastLoadedAt }: { lastLoadedAt: Date | null }): ReactNode {
  const { t } = useI18n();
  const [connection, setConnection] = useState<Connection>(() =>
    typeof navigator === 'undefined' || navigator.onLine ? 'online' : 'offline',
  );
  const [, setTick] = useState(0);

  useEffect(() => {
    const goOnline = (): void => setConnection('online');
    const goOffline = (): void => setConnection('offline');

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    /*
     * A slow tick while offline.
     *
     * The banner's wording changes once the screen has been stale for a minute, and without a tick
     * that change would only happen on the next render — which, on a screen nobody is touching, is
     * never.
     */
    const timer = window.setInterval(() => setTick((value) => value + 1), 15_000);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.clearInterval(timer);
    };
  }, []);

  const banner = bannerFor({ lastLoadedAt, connection, now: new Date() });
  if (banner === null) return null;

  return (
    <div
      className={`connection-banner connection-banner--${banner.tone}`}
      role="status"
      aria-live="polite"
    >
      {t(banner.key)}
    </div>
  );
}
